import { createSlice, Draft, isAnyOf } from '@reduxjs/toolkit'
import {
  OrderSummaryOrderItem,
  OrderSummaryOrderItemTicketItem,
  TicketItemStatus,
  TicketStatus,
} from '@ancon/wildcat-types'
import isEmpty from 'lodash/isEmpty'

import { OrderReducerState } from '../types'
import { signOut } from '../../auth/store/authThunks'
import extractCheckOrderTickets from '../utils/extractCheckOrderTickets'
import updateTicketItemsStatus from '../utils/updateTicketItemsStatus'
import updateTicketItemsStatuses from '../utils/updateTicketItemsStatuses'
import updateTicketStatusFromItemsStatus from '../utils/updateTicketStatusFromItemsStatus'

import {
  downloadOrderReceipt,
  fetchOngoingOrders,
  fetchOrderDetails,
  fetchOrdersList,
  fetchOrderSummary,
  fetchOrderTicketItemsStatus,
  fetchUpcomingOrders,
  markOrderAsPickedUp,
  markTicketItemAsPickedUp,
  sendOrderReceipt,
} from './ordersThunks'
import {
  activeOrdersEntityAdapter,
  checkOrderTicketsEntityAdapter,
  pastOrdersEntityAdapter,
  upcomingOrdersEntityAdapter,
} from './ordersEntityAdapter'
import ordersReducers from './ordersReducer'

const ordersInitialState: OrderReducerState = {
  activeOrders: activeOrdersEntityAdapter.getInitialState(),
  activeOrderFetchPending: true,

  pastOrders: pastOrdersEntityAdapter.getInitialState(),
  pastOrdersFetchPending: false,

  upcomingOrders: upcomingOrdersEntityAdapter.getInitialState(),
  upcomingOrdersFetchPending: false,

  orderDetailsFetchPending: false,
  orderDetails: null,

  checkOrderSummary: null,
  checkOrderSummaryTickets: checkOrderTicketsEntityAdapter.getInitialState(),
  checkOrderSummaryFetchPending: false,

  orderReceiptSendPending: false,
  isReceiptDownloadPending: false,
}

const ordersSlice = createSlice({
  name: 'orders',
  initialState: ordersInitialState,
  reducers: ordersReducers,
  extraReducers(builder) {
    // Download receipt
    builder.addCase(downloadOrderReceipt.pending, state => {
      state.isReceiptDownloadPending = true
    })

    // Fetch Active Orders
    builder.addCase(fetchOngoingOrders.pending, (state, action) => {
      if (!action.meta.arg?.isBackgroundFetch) {
        state.activeOrderFetchPending = true
      }
    })
    builder.addCase(fetchOngoingOrders.fulfilled, (state, action) => {
      activeOrdersEntityAdapter.setAll(state.activeOrders, action.payload.items)
      state.activeOrderFetchPending = false
    })
    builder.addCase(fetchOngoingOrders.rejected, state => {
      state.activeOrderFetchPending = false
    })

    // Fetch Past Orders
    builder.addCase(fetchOrdersList.pending, state => {
      state.pastOrdersFetchPending = true
    })
    builder.addCase(fetchOrdersList.fulfilled, (state, action) => {
      pastOrdersEntityAdapter.setAll(state.pastOrders, action.payload.items)
      state.pastOrdersFetchPending = false
    })
    builder.addCase(fetchOrdersList.rejected, state => {
      state.pastOrdersFetchPending = false
    })

    // Fetch Upcoming Orders
    builder.addCase(fetchUpcomingOrders.pending, state => {
      state.upcomingOrdersFetchPending = true
    })
    builder.addCase(fetchUpcomingOrders.fulfilled, (state, action) => {
      upcomingOrdersEntityAdapter.setAll(
        state.upcomingOrders,
        action.payload.items,
      )
      state.upcomingOrdersFetchPending = false
    })
    builder.addCase(fetchUpcomingOrders.rejected, state => {
      state.upcomingOrdersFetchPending = false
    })

    // Fetch order details
    builder.addCase(fetchOrderDetails.pending, state => {
      state.orderDetailsFetchPending = true
      state.orderDetails = null
    })
    builder.addCase(fetchOrderDetails.fulfilled, (state, action) => {
      const { payload } = action

      state.orderDetailsFetchPending = false
      state.orderDetails = payload
    })
    builder.addCase(fetchOrderDetails.rejected, state => {
      state.orderDetailsFetchPending = false
    })

    // On send order receipt start
    builder.addCase(sendOrderReceipt.pending, state => {
      state.orderReceiptSendPending = true
    })

    // Sign out
    builder.addCase(signOut.fulfilled, () => ordersInitialState)

    // On fetch order summary pending
    builder.addCase(fetchOrderSummary.pending, state => {
      state.checkOrderSummaryFetchPending = true
      state.checkOrderSummary = null
    })
    // On fetch order summary finish
    builder.addCase(fetchOrderSummary.fulfilled, (state, action) => {
      state.checkOrderSummaryFetchPending = false
      state.checkOrderSummary = action.payload

      const tickets = extractCheckOrderTickets(action.payload.items || [])
      checkOrderTicketsEntityAdapter.setAll(
        state.checkOrderSummaryTickets,
        tickets,
      )
    })
    // On fetch order summary error
    builder.addCase(fetchOrderSummary.rejected, state => {
      state.checkOrderSummaryFetchPending = false
    })

    // Ticket status, ticket item status updates from polling
    builder.addCase(fetchOrderTicketItemsStatus.fulfilled, (state, action) => {
      if (
        state.checkOrderSummary &&
        state.checkOrderSummary.id === action.meta.arg.orderId &&
        !isEmpty(action.payload.statuses)
      ) {
        // Update ticket items statuses
        const updatedTicketIds = updateTicketItemsStatuses(
          state,
          action.payload.statuses,
        )

        // Update tickets status to the minimum ticket item status
        updateTicketStatusFromItemsStatus(state, updatedTicketIds)
      }
    })

    // Mark order as picked up
    builder.addCase(markOrderAsPickedUp.fulfilled, (state, action) => {
      const { ticketId } = action.meta.arg

      function closeOrderTicketItem(
        ticketItem: Draft<OrderSummaryOrderItemTicketItem>,
      ) {
        if (
          ticketItem.ticketId === ticketId &&
          ticketItem.status < TicketItemStatus.Closed
        ) {
          ticketItem.status = TicketItemStatus.Closed
        }

        return ticketItem
      }

      if (state.checkOrderSummary) {
        const ticketEntity = state.checkOrderSummaryTickets.entities[ticketId]

        if (ticketEntity) {
          // STEP 1: Update ticket status and ticket items statuses as closed
          const updatedTicketItems = ticketEntity.ticketItems.map(
            ticketItem => {
              if (ticketItem.status < TicketItemStatus.Closed) {
                ticketItem.status = TicketItemStatus.Closed
              }
              return ticketItem
            },
          )

          checkOrderTicketsEntityAdapter.updateOne(
            state.checkOrderSummaryTickets,
            {
              id: ticketId,
              changes: {
                status: TicketStatus.Closed,
                ticketItems: updatedTicketItems,
              },
            },
          )

          // STEP 2: Update ticket items in order items
          state.checkOrderSummary.items = state.checkOrderSummary.items.reduce<
            OrderSummaryOrderItem[]
          >((acc, orderItem) => {
            const subProducts = orderItem.items
            const isMultiProductOrderItem = subProducts.length > 0

            // Close order item ticket items
            orderItem.ticketItems.forEach(closeOrderTicketItem)

            if (isMultiProductOrderItem) {
              // Close sub products ticket items
              subProducts.forEach(subProduct => {
                subProduct.ticketItems.forEach(closeOrderTicketItem)
              })
            }

            acc.push(orderItem)

            return acc
          }, [])
        }
      }
    })

    builder.addCase(markTicketItemAsPickedUp.fulfilled, (state, action) => {
      const { ticketId, ticketItemIds } = action.meta.arg

      if (state.checkOrderSummary) {
        // Update ticket items status as closed
        updateTicketItemsStatus(
          state,
          ticketId,
          ticketItemIds,
          TicketItemStatus.Closed,
        )

        // Update ticket status to the minimum ticket item status if needed
        updateTicketStatusFromItemsStatus(state, [ticketId])
      }
    })

    // On download order receipt finish
    builder.addMatcher(
      isAnyOf(downloadOrderReceipt.fulfilled, downloadOrderReceipt.rejected),
      state => {
        state.isReceiptDownloadPending = false
      },
    )
    // On send order receipt finish
    builder.addMatcher(
      isAnyOf(sendOrderReceipt.fulfilled, sendOrderReceipt.rejected),
      state => {
        state.orderReceiptSendPending = false
      },
    )
  },
})

export const {
  ordersClearOrderDetails,
  customerOrdersEmitCheckoutStatusEvent,
  customerOrdersEmitTicketItemStatusEvent,
  checkOrderEmitTicketStatusUpdatedEvent,
  checkOrderEmitTicketItemStatusUpdatedEvent,
} = ordersSlice.actions

export default ordersSlice.reducer
